/**
 * Platform Capabilities configuration API client (REQ-CAP-002, ADR-113).
 *
 * Endpoints del Sprint 255 D2 (respuestas sin wrapper — dicts planos):
 *   GET  /api/v1/tenants/{t}/capabilities                        → catálogo + política tenant
 *   PUT  /api/v1/tenants/{t}/capabilities/{slug}                 → política del tenant
 *   GET  /api/v1/tenants/{t}/capabilities/hives/{id}             → estado del hive
 *   PUT  /api/v1/tenants/{t}/capabilities/hives/{id}             → decisiones del hive
 *   GET  /api/v1/tenants/{t}/capabilities/resolve/agent/{id}     → resolución efectiva
 */

import apiClient from '@/lib/api-client';
import type {
  CapabilityCatalogEntry,
  EffectiveCapability,
  HiveCapabilitiesResponse,
  HiveCapabilityDecisionInput,
  TenantPolicyInput,
} from './types';

interface CapabilitiesListResponse {
  capabilities: CapabilityCatalogEntry[];
}

export async function fetchCapabilitiesCatalog(tenantId: string): Promise<CapabilityCatalogEntry[]> {
  const { data } = await apiClient.get<CapabilitiesListResponse>(
    `/api/v1/tenants/${tenantId}/capabilities`,
  );
  return data.capabilities ?? [];
}

export async function fetchAgentCapabilities(
  tenantId: string,
  agentId: string,
): Promise<Record<string, EffectiveCapability>> {
  const { data } = await apiClient.get<{ effective: Record<string, EffectiveCapability> }>(
    `/api/v1/tenants/${tenantId}/capabilities/resolve/agent/${agentId}`,
  );
  return data.effective ?? {};
}

export async function fetchHiveCapabilities(
  tenantId: string,
  hiveId: string,
): Promise<HiveCapabilitiesResponse> {
  const { data } = await apiClient.get<HiveCapabilitiesResponse>(
    `/api/v1/tenants/${tenantId}/capabilities/hives/${hiveId}`,
  );
  return data;
}

export async function putHiveDecisions(
  tenantId: string,
  hiveId: string,
  decisions: HiveCapabilityDecisionInput[],
): Promise<{ hive_id: string; decisions: Record<string, unknown> }> {
  const { data } = await apiClient.put<{ hive_id: string; decisions: Record<string, unknown> }>(
    `/api/v1/tenants/${tenantId}/capabilities/hives/${hiveId}`,
    { decisions },
  );
  return data;
}

export async function putTenantPolicy(
  tenantId: string,
  slug: string,
  input: TenantPolicyInput,
): Promise<{ slug: string; enabled: boolean; settings: Record<string, unknown> }> {
  const { data } = await apiClient.put<{
    slug: string;
    enabled: boolean;
    settings: Record<string, unknown>;
  }>(`/api/v1/tenants/${tenantId}/capabilities/${slug}`, input);
  return data;
}
